// Iter 77 — Vendor Service Invoices (lightweight per-service tracker).
// Rendered inside the expand row on VendorServicesTab. Independent of
// Vendor Bills/Payments procurement workflow.
import { useEffect, useRef, useState } from "react";
import {
    Plus, FileArrowUp, DownloadSimple, Trash, PencilSimple,
    Receipt, CheckCircle, XCircle, Paperclip,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
    SERVICE_INVOICE_STATUS_LABEL, SERVICE_INVOICE_STATUS_BADGE,
    formatAmount, humanBytes,
} from "./vendor_constants";


const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (d) => { const x = new Date(); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); };


export default function VendorServiceInvoicesPanel({ vendor, service }) {
    const { can } = useAuth();
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({ paid_total: 0, unpaid_total: 0, paid_count: 0, unpaid_count: 0, cancelled_count: 0 });
    const [loading, setLoading] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const reload = () => setReloadKey((k) => k + 1);

    useEffect(() => {
        let cancelled = false;
        api.get(`/vendors/${vendor.id}/services/${service.id}/invoices`)
            .then(({ data }) => {
                if (cancelled) return;
                setRows(data.items || []);
                setSummary(data.summary || {});
            })
            .catch((err) => { if (!cancelled) toast.error(formatApiError(err, "Failed to load invoices")); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [vendor.id, service.id, reloadKey]);

    const openCreate = () => { setEditing(null); setDialogOpen(true); };
    const openEdit = (inv) => { setEditing(inv); setDialogOpen(true); };

    const setStatus = async (inv, nextStatus) => {
        try {
            await api.patch(`/vendor-service-invoices/${inv.id}/status`, { status: nextStatus });
            toast.success(`Marked ${SERVICE_INVOICE_STATUS_LABEL[nextStatus]}`);
            reload();
        } catch (err) { toast.error(formatApiError(err, "Status change failed")); }
    };

    const removeInvoice = async (inv) => {
        if (!window.confirm(`Delete invoice ${inv.code} (${inv.invoice_number})? This cannot be undone.`)) return;
        try {
            await api.delete(`/vendor-service-invoices/${inv.id}`);
            toast.success("Invoice deleted");
            reload();
        } catch (err) { toast.error(formatApiError(err, "Delete failed")); }
    };

    if (loading) {
        return <div className="text-xs text-slate-500 py-4 text-center" data-testid={`vsi-loading-${service.code}`}>Loading invoices…</div>;
    }

    return (
        <div className="bg-slate-50/50 border-t border-slate-200" data-testid={`vsi-panel-${service.code}`}>
            <div className="px-4 py-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div>
                        <h4 className="text-sm font-medium text-slate-900 inline-flex items-center gap-2">
                            <Receipt size={14} className="text-slate-500" /> Invoices
                        </h4>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                            Lightweight per-service invoice tracker. Independent of Vendor Bills / Payments.
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {rows.length > 0 && (
                            <div className="flex items-center gap-2 text-[11px]" data-testid={`vsi-summary-${service.code}`}>
                                <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">
                                    Paid: {formatAmount(summary.paid_total, service.currency)}
                                </Badge>
                                <Badge variant="secondary" className="bg-amber-50 text-amber-700 hover:bg-amber-50">
                                    Unpaid: {formatAmount(summary.unpaid_total, service.currency)}
                                </Badge>
                            </div>
                        )}
                        {can("vendor:write") && (
                            <Button data-testid={`vsi-add-btn-${service.code}`} size="sm" onClick={openCreate} className="gap-1 h-7">
                                <Plus size={12} /> Add Invoice
                            </Button>
                        )}
                    </div>
                </div>

                {rows.length === 0 ? (
                    <div className="text-center py-6 border border-dashed border-slate-200 rounded-md bg-white" data-testid={`vsi-empty-${service.code}`}>
                        <Receipt size={20} className="text-slate-300 mx-auto mb-1" />
                        <div className="text-xs text-slate-500">No invoices recorded</div>
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-md border border-slate-200 bg-white" data-testid={`vsi-table-${service.code}`}>
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
                                <tr>
                                    <th className="text-left font-medium px-3 py-2">Code</th>
                                    <th className="text-left font-medium px-3 py-2">Invoice #</th>
                                    <th className="text-left font-medium px-3 py-2">Inv Date</th>
                                    <th className="text-left font-medium px-3 py-2">Due Date</th>
                                    <th className="text-right font-medium px-3 py-2">Amount</th>
                                    <th className="text-right font-medium px-3 py-2">GST</th>
                                    <th className="text-right font-medium px-3 py-2">TDS</th>
                                    <th className="text-center font-medium px-3 py-2">PDF</th>
                                    <th className="text-left font-medium px-3 py-2">Status</th>
                                    {can("vendor:write") && <th className="w-20" />}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((inv) => {
                                    const badge = SERVICE_INVOICE_STATUS_BADGE[inv.status] || SERVICE_INVOICE_STATUS_BADGE.unpaid;
                                    return (
                                        <tr key={inv.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`vsi-row-${inv.code}`}>
                                            <td className="px-3 py-2 font-mono text-[11px] text-slate-700">{inv.code}</td>
                                            <td className="px-3 py-2 text-slate-900">{inv.invoice_number}</td>
                                            <td className="px-3 py-2 text-slate-700">{formatDate(inv.invoice_date)}</td>
                                            <td className="px-3 py-2 text-slate-700">{formatDate(inv.due_date)}</td>
                                            <td className="px-3 py-2 text-right font-mono text-slate-900">{formatAmount(inv.amount, inv.currency)}</td>
                                            <td className="px-3 py-2 text-right font-mono text-slate-600">
                                                {Number(inv.gst_amount) > 0 ? (
                                                    <span data-testid={`vsi-gst-${inv.code}`}>
                                                        {formatAmount(inv.gst_amount, inv.currency)}
                                                        {Number(inv.gst_percent) > 0 && (
                                                            <span className="text-slate-400 text-[10px] ml-1">@{inv.gst_percent}%</span>
                                                        )}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-300">—</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono text-slate-600">
                                                {Number(inv.tds_amount) > 0 ? formatAmount(inv.tds_amount, inv.currency) : <span className="text-slate-300">—</span>}
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                {inv.attachment ? (
                                                    <a data-testid={`vsi-pdf-link-${inv.code}`} href={inv.attachment.file_url} target="_blank" rel="noreferrer"
                                                        className="inline-flex items-center gap-1 text-indigo-600 hover:underline">
                                                        <Paperclip size={11} /> PDF
                                                    </a>
                                                ) : (
                                                    <span className="text-slate-300">—</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2">
                                                <Badge data-testid={`vsi-status-${inv.code}`} className={`${badge.className} hover:${badge.className} text-[10px]`}>
                                                    {badge.label}
                                                </Badge>
                                            </td>
                                            {can("vendor:write") && (
                                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                                    <RowActions invoice={inv} onEdit={() => openEdit(inv)} onStatus={setStatus} onDelete={removeInvoice} />
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <InvoiceDialog
                key={editing?.id || "create"}
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSaved={reload}
                vendor={vendor}
                service={service}
                editing={editing}
            />
        </div>
    );
}


function RowActions({ invoice, onEdit, onStatus, onDelete }) {
    const showMarkPaid = invoice.status === "unpaid";
    const showMarkUnpaid = invoice.status === "paid";
    const showCancel = invoice.status !== "cancelled";
    return (
        <div className="flex items-center gap-0.5 justify-end">
            {showMarkPaid && (
                <button
                    data-testid={`vsi-mark-paid-${invoice.code}`}
                    onClick={() => onStatus(invoice, "paid")}
                    title="Mark Paid"
                    className="p-1 rounded text-emerald-600 hover:bg-emerald-50"
                ><CheckCircle size={14} /></button>
            )}
            {showMarkUnpaid && (
                <button
                    data-testid={`vsi-mark-unpaid-${invoice.code}`}
                    onClick={() => onStatus(invoice, "unpaid")}
                    title="Mark Unpaid"
                    className="p-1 rounded text-amber-600 hover:bg-amber-50"
                ><XCircle size={14} /></button>
            )}
            {invoice.status !== "cancelled" && (
                <button
                    data-testid={`vsi-edit-${invoice.code}`}
                    onClick={onEdit}
                    title="Edit"
                    className="p-1 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                ><PencilSimple size={14} /></button>
            )}
            {showCancel && (
                <button
                    data-testid={`vsi-cancel-${invoice.code}`}
                    onClick={() => {
                        if (window.confirm(`Cancel invoice ${invoice.code}?`)) onStatus(invoice, "cancelled");
                    }}
                    title="Cancel"
                    className="p-1 rounded text-rose-500 hover:bg-rose-50"
                ><XCircle size={14} /></button>
            )}
            <button
                data-testid={`vsi-delete-${invoice.code}`}
                onClick={() => onDelete(invoice)}
                title="Delete"
                className="p-1 rounded text-rose-600 hover:bg-rose-50"
            ><Trash size={14} /></button>
        </div>
    );
}


function InvoiceDialog({ open, onClose, onSaved, vendor, service, editing }) {
    const isEdit = !!editing;
    const fileRef = useRef(null);
    const [form, setForm] = useState(() => ({
        invoice_number: editing?.invoice_number || "",
        invoice_date: editing?.invoice_date || todayISO(),
        due_date: editing?.due_date || addDaysISO(30),
        amount: editing?.amount ?? "",
        gst_percent: editing?.gst_percent ?? 0,
        gst_amount: editing?.gst_amount ?? 0,
        tds_amount: editing?.tds_amount ?? 0,
        status: editing?.status || "unpaid",
        notes: editing?.notes || "",
    }));
    const [busy, setBusy] = useState(false);
    // Track whether the operator manually overrode the GST amount field.
    // When false, GST amount auto-computes from amount × gst_percent inline.
    const [gstAmountOverridden, setGstAmountOverridden] = useState(
        !!(editing?.gst_amount && editing?.gst_percent &&
            Math.abs(Number(editing.gst_amount) - (Number(editing.amount) * Number(editing.gst_percent) / 100)) > 0.01)
    );

    const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value }));

    // Inline GST autocompute — runs on amount / gst_percent change unless overridden.
    const recomputeGst = (next) => {
        if (gstAmountOverridden) return next;
        const amt = Number(next.amount || 0);
        const pct = Number(next.gst_percent || 0);
        const gst = amt > 0 && pct > 0 ? +(amt * pct / 100).toFixed(2) : 0;
        return { ...next, gst_amount: gst };
    };

    const submit = async () => {
        if (!form.invoice_number.trim()) { toast.error("Invoice number required"); return; }
        if (!form.invoice_date || !form.due_date) { toast.error("Dates required"); return; }
        if (form.due_date < form.invoice_date) { toast.error("Due date must be on or after invoice date"); return; }
        if (Number(form.amount || 0) <= 0) { toast.error("Amount must be > 0"); return; }
        if (Number(form.tds_amount || 0) > Number(form.amount || 0)) {
            toast.error("TDS cannot exceed amount"); return;
        }
        if (Number(form.gst_amount || 0) > Number(form.amount || 0)) {
            toast.error("GST cannot exceed amount"); return;
        }
        if (Number(form.gst_percent || 0) < 0 || Number(form.gst_percent || 0) > 100) {
            toast.error("GST % must be between 0 and 100"); return;
        }
        setBusy(true);
        const payload = {
            invoice_number: form.invoice_number.trim(),
            invoice_date: form.invoice_date,
            due_date: form.due_date,
            amount: Number(form.amount),
            tds_amount: Number(form.tds_amount || 0),
            gst_amount: Number(form.gst_amount || 0),
            gst_percent: Number(form.gst_percent || 0),
            status: form.status,
            notes: form.notes,
        };
        try {
            let saved;
            if (isEdit) {
                const r = await api.put(`/vendor-service-invoices/${editing.id}`, payload);
                saved = r.data;
                toast.success("Invoice updated");
            } else {
                const r = await api.post(`/vendors/${vendor.id}/services/${service.id}/invoices`, payload);
                saved = r.data;
                toast.success(`Invoice ${r.data.code} created`);
            }
            // Optional PDF upload
            const file = fileRef.current?.files?.[0];
            if (file) {
                if (file.size > 10 * 1024 * 1024) {
                    toast.warning("Invoice saved but PDF exceeds 10 MB and was skipped.");
                } else if (file.type && file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
                    toast.warning("Invoice saved but attachment skipped — only PDF is accepted.");
                } else {
                    const fd = new FormData();
                    fd.append("file", file);
                    try {
                        await api.post(`/vendor-service-invoices/${saved.id}/attachment`, fd, { headers: { "Content-Type": undefined } });
                    } catch (err) {
                        toast.error(formatApiError(err, "PDF upload failed (record saved)"));
                    }
                }
            }
            onSaved();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        } finally { setBusy(false); }
    };

    const removeAttachment = async () => {
        if (!editing?.attachment) return;
        if (!window.confirm("Remove the attached PDF?")) return;
        try {
            await api.delete(`/vendor-service-invoices/${editing.id}/attachment`);
            toast.success("Attachment removed");
            onSaved();
            onClose();
        } catch (err) { toast.error(formatApiError(err, "Remove failed")); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="vsi-dialog">
                <DialogHeader>
                    <DialogTitle>{isEdit ? `Edit Invoice ${editing.code}` : "Add Invoice"}</DialogTitle>
                    <DialogDescription>
                        For {service.name} ({service.code}) · {vendor.name}.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
                    <Field label="Invoice Number *" colSpan={2}>
                        <Input data-testid="vsi-input-number" value={form.invoice_number} onChange={setField("invoice_number")} placeholder="Vendor's invoice ref" />
                    </Field>
                    <Field label="Invoice Date *">
                        <Input data-testid="vsi-input-invoice-date" type="date" value={form.invoice_date} onChange={setField("invoice_date")} />
                    </Field>
                    <Field label="Due Date *">
                        <Input data-testid="vsi-input-due-date" type="date" value={form.due_date} onChange={setField("due_date")} />
                    </Field>
                    <Field label={`Amount * (${service.currency || "INR"})`}>
                        <Input
                            data-testid="vsi-input-amount" type="number" min={0} step="0.01"
                            value={form.amount}
                            onChange={(e) => setForm((f) => recomputeGst({ ...f, amount: e.target.value }))}
                        />
                    </Field>
                    <Field label="GST %" hint="Leave 0 if non-taxable. GST Amount auto-computes.">
                        <Input
                            data-testid="vsi-input-gst-percent"
                            type="number" min={0} max={100} step="0.01"
                            value={form.gst_percent}
                            onChange={(e) => {
                                setGstAmountOverridden(false);
                                setForm((f) => recomputeGst({ ...f, gst_percent: e.target.value }));
                            }}
                            placeholder="e.g. 18"
                        />
                    </Field>
                    <Field label="GST Amount" hint="Edit to override the auto-computed value.">
                        <Input
                            data-testid="vsi-input-gst-amount"
                            type="number" min={0} step="0.01"
                            value={form.gst_amount}
                            onChange={(e) => {
                                setGstAmountOverridden(true);
                                setForm((f) => ({ ...f, gst_amount: e.target.value }));
                            }}
                        />
                    </Field>
                    <Field label="TDS Deducted (optional)" hint="Leave 0 if not applicable">
                        <Input data-testid="vsi-input-tds" type="number" min={0} step="0.01" value={form.tds_amount} onChange={setField("tds_amount")} />
                    </Field>
                    <Field label="Status">
                        <select data-testid="vsi-input-status" value={form.status} onChange={setField("status")}
                            className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-sm shadow-sm">
                            <option value="unpaid">Unpaid</option>
                            <option value="paid">Paid</option>
                            {isEdit && <option value="cancelled">Cancelled</option>}
                        </select>
                    </Field>
                    <Field label="PDF Attachment (optional, max 10 MB)">
                        <input data-testid="vsi-input-pdf" ref={fileRef} type="file" accept=".pdf,application/pdf"
                            className="block w-full text-sm text-slate-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border file:border-slate-300 file:bg-white file:text-slate-700 file:cursor-pointer" />
                        {isEdit && editing.attachment && (
                            <div className="mt-2 flex items-center justify-between gap-2 text-xs bg-slate-50 px-3 py-2 rounded-md" data-testid={`vsi-current-attachment-${editing.code}`}>
                                <div className="min-w-0 flex-1">
                                    <a href={editing.attachment.file_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-indigo-600 hover:underline truncate">
                                        <DownloadSimple size={11} /> {editing.attachment.file_name}
                                    </a>
                                    <div className="text-[10px] text-slate-500 mt-0.5">{humanBytes(editing.attachment.size_bytes)}</div>
                                </div>
                                <button onClick={removeAttachment} data-testid={`vsi-attachment-remove-${editing.code}`}
                                    className="text-rose-600 hover:bg-rose-50 p-1 rounded">
                                    <Trash size={12} />
                                </button>
                            </div>
                        )}
                    </Field>
                    <Field label="Notes" colSpan={2}>
                        <Textarea data-testid="vsi-input-notes" rows={2} value={form.notes} onChange={setField("notes")} />
                    </Field>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button data-testid="vsi-save-btn" onClick={submit} disabled={busy}>
                        {busy ? "Saving…" : isEdit ? "Update Invoice" : "Create Invoice"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function Field({ label, hint, children, colSpan }) {
    return (
        <div className={colSpan === 2 ? "sm:col-span-2" : ""}>
            <Label className="text-xs text-slate-600 mb-1.5 inline-block">{label}</Label>
            {children}
            {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
        </div>
    );
}
